import { QueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';

/**
 * Type guard for API response errors from @hey-api/client-fetch.
 * The new client returns { data, error, response } — when throwOnError is used,
 * errors are thrown as-is. Check for response-like objects with status.
 */
function isApiResponseError(error: unknown): error is { status: number; body?: unknown; message?: string } {
  return error !== null && typeof error === 'object' && 'status' in error;
}

// Type guard to safely extract message from error body
function getErrorMessage(error: unknown): string {
  if (isApiResponseError(error)) {
    const body = error.body
    if (
      body !== null &&
      typeof body === 'object' &&
      'message' in body &&
      typeof (body as Record<string, unknown>).message === 'string'
    ) {
      return String((body as Record<string, unknown>).message)
    }
    if (typeof error.message === 'string') {
      return error.message
    }
    return `HTTP ${error.status}`
  }
  if (error instanceof Error) {
    return error.message
  }
  return 'An error occurred'
}

/**
 * Extract HTTP status from various error shapes.
 * The new @hey-api/client-fetch may throw errors as plain objects with status,
 * or as Error instances with a status property attached.
 */
function getErrorStatus(error: unknown): number | undefined {
  if (isApiResponseError(error)) {
    return error.status;
  }
  // Some errors may have status attached as a property on Error
  if (error instanceof Error && 'status' in error) {
    return (error as Error & { status: number }).status;
  }
  return undefined;
}

// Handle 401 errors — the response interceptor in apiClient.ts handles token refresh + retry,
// so by the time React Query sees a 401, the interceptor already tried and failed.
// This handler is a safety net that clears auth when refresh is truly impossible.
const handle401Error = (error: unknown): void => {
  const { clearAuth } = useAuthStore.getState();
  const status = getErrorStatus(error);
  if (status === 401) {
    console.log('[QueryClient] 401 reached React Query — interceptor retry likely failed, clearing auth');
    clearAuth();
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }
};

// Create a client
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount: number, error: Error): boolean => {
        const status = getErrorStatus(error);
        if (status !== undefined) {
          if (status === 401) {
            // The response interceptor already attempted refresh + retry.
            // Allow one React Query retry in case the token was just refreshed by another tab.
            if (failureCount < 1) {
              return true;
            }
            handle401Error(error);
            return false;
          }
          // Never retry on server errors (e.g., 5xx) per product requirement
          if (status >= 500) {
            return false;
          }
        }
        // Otherwise allow up to 2 retries for transient cases (e.g., network hiccup)
        return failureCount < 2;
      },
      // Five minutes stale time
      staleTime: 5 * 60 * 1000,
      // Thirty minutes garbage collection time (formerly cacheTime)
      // Longer gcTime keeps data in cache for instant display when users
      // navigate back to pages, even after data becomes stale.
      gcTime: 30 * 60 * 1000,
    },
    mutations: {
      onError: async (error: Error): Promise<void> => {
        // Global error handling for mutations
        const status = getErrorStatus(error);
        if (status !== undefined) {
          if (status === 401) {
            handle401Error(error);
            return;
          }
          const message = getErrorMessage(error);
          console.error('API Error:', message);
        } else {
          console.error('Unexpected error:', error);
        }
      },
    },
  },
});
